/**
 * DisputedCarousel — horizontal carousel 4-5 cards (§11.1 / DM-22).
 *
 * Block M1a-FE (TZ 2026-07-29). NOT «show more».
 * Horizontal scroll-snap: ширина карточки фиксирована, overflow-x-auto.
 * Nav buttons (←/→) используют scrollBy. Remaining counter в header.
 *
 * Carousel = только status='disputed' (§11.4). После Confirm — ряд уходит
 * (resolveRow invalidates queries → usePipelineMatchingRows refetch → status
 * меняется на matched → filtered out).
 *
 * Source of truth: per-row entry — `r.mapped_data.rows[r.id]?.status === 'disputed'`.
 * BE writer mirrors full `mapped_data.rows` onto every DatasetRow JSONB; we
 * MUST read by `r.id`, not iterate, or we'd emit N×disputed cards with the
 * same row_id repeated.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { DisputedCard, type DisputedCardData } from './DisputedCard';
import { usePipelineMatchingRows } from '@/hooks/useMatching';

export interface DisputedCarouselProps {
  pipelineId: string | null;
  datasetId: string | null;
  onOpenEye: (data: DisputedCardData) => void;
}

const RAW_KEY_COLUMN_CANDIDATES = ['name', 'title', 'product', 'sku'];

function pickDisplayName(raw: Record<string, unknown> | null): string {
  if (!raw) return '';
  for (const k of RAW_KEY_COLUMN_CANDIDATES) {
    const v = raw[k];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  for (const [, v] of Object.entries(raw)) {
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

export function DisputedCarousel({
  pipelineId,
  datasetId,
  onOpenEye,
}: DisputedCarouselProps) {
  const { t } = useTranslation();
  const { data: rowsResp } = usePipelineMatchingRows(pipelineId);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // §C matching-v2: «Показати ще 12» — paginate heavy lists in steps of 12.
  const [visibleLimit, setVisibleLimit] = useState(12);

  const disputed: DisputedCardData[] = useMemo(() => {
    const rows = rowsResp?.rows ?? [];
    const out: DisputedCardData[] = [];
    for (const r of rows) {
      const md = r.mapped_data as
        | {
            rows?: Record<
              string,
              {
                status?: string;
                candidates?: import('@/hooks/useMatching').MatchingCategoryCandidate[];
              }
            >;
          }
        | null
        | undefined;
      // Per-row: read the entry keyed by THIS row's own UUID (`r.id`).
      // `row_id === DatasetRow.id` is the writer invariant (writer uses
      // `str(row_id)` as dict key, so r.id (UUID string) matches).
      const entry = md?.rows?.[r.id];
      if (entry?.status !== 'disputed') continue;
      const candidates = (entry.candidates ?? [])
        .slice()
        .sort((a, b) => b.score - a.score);
      if (candidates.length === 0) continue;
      out.push({
        row_id: r.id,
        displayName: pickDisplayName(
          r.raw_data as Record<string, unknown> | null,
        ),
        candidates,
        rowIndex: r.row_index ?? null,
      });
    }
    return out;
  }, [rowsResp]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
  }, [disputed.length]);

  if (!pipelineId || disputed.length === 0) return null;

  function scrollBy(dir: 1 | -1) {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth - 80), behavior: 'smooth' });
  }

  return (
    <section className="card space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400">
          {t('process.matching.results.disputed.title', {
            defaultValue: 'Disputed products',
          })}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-500">{disputed.length}</span>
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            disabled={disputed.length <= 1}
            className="p-1 rounded text-surface-400 hover:text-surface-100 hover:bg-surface-800 transition-colors disabled:opacity-40"
            aria-label="prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            disabled={disputed.length <= 1}
            className="p-1 rounded text-surface-400 hover:text-surface-100 hover:bg-surface-800 transition-colors disabled:opacity-40"
            aria-label="next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth"
        data-testid="disputed-carousel"
      >
        {disputed.slice(0, visibleLimit).map((d) => (
          <DisputedCard
            key={d.row_id}
            data={d}
            datasetId={datasetId}
            onOpenEye={onOpenEye}
          />
        ))}
      </div>

      {disputed.length > visibleLimit && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleLimit((n) => n + 12)}
            className="btn-secondary text-xs px-3 py-1.5"
            data-testid="disputed-show-more"
          >
            {t('process.matching.disputed.showMore', {
              defaultValue: 'Показати ще 12',
              count: Math.min(12, disputed.length - visibleLimit),
            })}
          </button>
        </div>
      )}
    </section>
  );
}
